// Spend/WaterfallChart.jsx
import { Box, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
  LabelList, ResponsiveContainer,
} from "recharts";
import theme from "../../../../theme";
import { buildWaterfall } from "./waterfall";

const COLORS = {
  increase: theme.palette.secondary.lawnGreen, // #AED135
  decrease: theme.palette.secondary.chilliRed, // #F40058
  total:    theme.palette.primary.main,        // #151A40
};
const FONT = theme.typography.fontFamily;

// adaptive £ formatter: £1.23M / £45k / £830
const formatGBP = (v) => {
  const n = Number(v) || 0;
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${sign}£${(a / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `${sign}£${(a / 1_000).toFixed(0)}k`;
  return `${sign}£${a.toLocaleString()}`;
};

// single-line themed tooltip (avoids double text from base + bar series)
const WaterfallTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Box
      sx={{
        fontFamily: FONT,
        backgroundColor: theme.palette.primary.white,
        border: `1px solid ${theme.palette.gray.medium}`,
        borderRadius: theme.borderRadius.small,
        boxShadow: theme.customShadows.medium,
        px: 1.5, py: 1,
      }}
    >
      <Typography variant="body2" sx={{ color: "primary.main", fontFamily: FONT }}>
        {row.name} : {formatGBP(row.value)}
      </Typography>
    </Box>
  );
};

// wraps a label into up to 2 lines by word count
const WrappedTick = ({ x, y, payload }) => {
  const words = String(payload.value).split(" ");
  const lines = [];
  let current = "";

  // greedy wrap ~20 chars per line, max 2 lines
  words.forEach((w) => {
    if ((current + " " + w).trim().length <= 22) {
      current = (current + " " + w).trim();
    } else {
      lines.push(current);
      current = w;
    }
  });
  if (current) lines.push(current);

  const shown = lines.slice(0, 2);
  // add ellipsis if we truncated
  if (lines.length > 2) shown[1] = shown[1].slice(0, 20) + "…";

  return (
    <text
      x={x}
      y={y + 10}
      textAnchor="middle"
      style={{ fontFamily: FONT, fontSize: 11, fill: theme.palette.primary.main }}
    >
      {shown.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : 12}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

export const WaterfallChart = ({ columns, rows, height = 320 }) => {
  const muiTheme = useTheme();
  const isSmall = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [labelCol, valueCol] = columns;
  const data = buildWaterfall(rows, labelCol.key, valueCol.key);

  if (!data.length) {
    return (
      <Typography sx={{ color: "text.secondary" }}>
        No commodity inflation data available.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      {/* Legend (MUI, matches donut approach) */}
      <Stack direction="row" spacing={2.5} sx={{ px: 1 }}>
        {[
          ["Increase", COLORS.increase],
          ["Decrease", COLORS.decrease],
          ["Total", COLORS.total],
        ].map(([label, color]) => (
          <Stack key={label} direction="row" sx={{ alignItems: "center" }} spacing={0.75}>
            <Box sx={{ width: 11, height: 11, borderRadius: "50%", backgroundColor: color }} />
            <Typography variant="caption" sx={{ color: "primary.main", fontFamily: FONT }}>
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Box sx={{ width: "100%", height, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 24, right: 12, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.gray.medium} />
            <XAxis
                dataKey="name"
                interval={0}
                tickLine={false}
                axisLine={{ stroke: theme.palette.gray.medium }}
                tick={isSmall ? false : <WrappedTick />}
                height={isSmall ? 8 : 44}     // taller to fit 2 lines
            />
            <YAxis
              tickFormatter={formatGBP}
              tick={{ fontFamily: FONT, fontSize: 12, fill: theme.palette.primary.main60 }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip cursor={{ fill: "transparent" }} content={<WaterfallTooltip />} />

            {/* invisible base that lifts each bar to its floating position */}
            <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />

            {/* visible colored segment */}
            <Bar dataKey="bar" stackId="wf" isAnimationActive={false} radius={[3, 3, 0, 0]}>
              {data.map((d, i) => (
                <Cell key={i} fill={COLORS[d.type]} />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                formatter={formatGBP}
                style={{ fontFamily: FONT, fontSize: 12, fill: theme.palette.primary.main }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Stack>
  );
};